// Translates the engineer-facing `ReceiptNature` enum into treasurer-first
// display labels at the render boundary. Sibling of `expenditureCategoryLabel` /
// `jurisdictionLabels`: a pure, React-free map so it can be reused across
// packages and snapshot-tested cheaply. Per the treasurer-first copy rule, the
// compound PascalCase identifiers (`FederalTransfer`, `InKind`) must never render
// verbatim; single-word identifiers that already read as plain English (`Other`,
// `Cash`) keep their word. This is a shared UTILITY other surfaces reuse — it is
// NOT the ledger's collapsed-row column display.

import { ReceiptNature } from '../generated/enums.gen';

/**
 * Per-type treasurer-first label, keyed by the wire string. Includes BOTH the
 * surviving `ReceiptNature` values AND the four RETIRED gift-source string keys
 * (`Individual` / `Pac` / `Party` / `Corporate`).
 *
 * The four retired keys are retained ONLY so historical/audit strings persisted
 * before ADR-057/#344 — when a gift still carried a person-vs-organization identity
 * — keep rendering a friendly word (the person/org identity now lives on the
 * contact's `ContactKind` + `OrganizationType`; a plain monetary gift is `Cash`).
 * No live write path produces these four values anymore. A new `ReceiptNature`
 * member without an entry falls back to its raw value via
 * {@link contributionTypeLabel} (and shows up in the snapshot test).
 */
export const CONTRIBUTION_TYPE_LABELS: Record<string, string> = {
  // Surviving ReceiptNature members.
  [ReceiptNature.InKind]: 'In-Kind',
  [ReceiptNature.FederalTransfer]: 'Federal Transfer',
  [ReceiptNature.OffsetToOperating]: 'Offset to Operating Expenditure',
  [ReceiptNature.OtherReceipt]: 'Other Receipt',
  [ReceiptNature.Other]: 'Other',
  [ReceiptNature.Cash]: 'Cash',
  // Retired gift-source string keys — audit-retention only (ADR-057/#344). These are
  // no longer `ReceiptNature` members, so they're keyed by their raw persisted string.
  Individual: 'Individual',
  Pac: 'PAC',
  Party: 'Party',
  Corporate: 'Corporate',
};

/**
 * Resolves the treasurer-first display label for a `ReceiptNature` (or a raw
 * persisted gift-type string, including the four retired sources). Falls back to
 * the raw value for any unexpected input so the boundary never renders empty.
 */
export function contributionTypeLabel(type: ReceiptNature | string): string {
  return CONTRIBUTION_TYPE_LABELS[type] ?? type;
}
